import React, { useState, useEffect, useMemo } from 'react'
import './Styles/errorFallback.scss'
import { ArrowLeft, RefreshCw, ShieldAlert, Search, Zap, ServerCrash, Lock, Clock, Home } from 'lucide-react'
import errorBg from './images/nice.jpg'

const ERROR_CONFIG = {
    401: {
        code: '401',
        title: 'Unauthorized',
        subtitle: 'Your session has expired or you don\'t have permission to access this resource.',
        icon: Lock,
        color: '#f59e0b',
        gradient: 'linear-gradient(135deg, #fef3c7, #fde68a)',
        darkGradient: 'linear-gradient(135deg, rgba(245, 158, 11, 0.15), rgba(217, 119, 6, 0.1))'
    },
    403: {
        code: '403',
        title: 'Access Denied',
        subtitle: 'You don\'t have the necessary permissions to view this page.',
        icon: ShieldAlert,
        color: '#ef4444',
        gradient: 'linear-gradient(135deg, #fee2e2, #fca5a5)',
        darkGradient: 'linear-gradient(135deg, rgba(239, 68, 68, 0.15), rgba(220, 38, 38, 0.1))'
    },
    404: {
        code: '404',
        title: 'Page Not Found',
        subtitle: 'The page you\'re looking for doesn\'t exist or has been moved.',
        icon: Search,
        color: '#8b5cf6',
        gradient: 'linear-gradient(135deg, #ede9fe, #c4b5fd)',
        darkGradient: 'linear-gradient(135deg, rgba(139, 92, 246, 0.15), rgba(124, 58, 237, 0.1))'
    },
    429: {
        code: '429',
        title: 'Too Many Requests',
        subtitle: 'You\'ve made too many requests. Please wait a moment and try again.',
        icon: Clock,
        color: '#0ea5e9',
        gradient: 'linear-gradient(135deg, #e0f2fe, #7dd3fc)',
        darkGradient: 'linear-gradient(135deg, rgba(14, 165, 233, 0.15), rgba(2, 132, 199, 0.1))'
    },
    500: {
        code: '500',
        title: 'Server Error',
        subtitle: 'Something went wrong on our end. We\'re working to fix it.',
        icon: ServerCrash,
        color: '#ef4444',
        gradient: 'linear-gradient(135deg, #fee2e2, #fca5a5)',
        darkGradient: 'linear-gradient(135deg, rgba(239, 68, 68, 0.15), rgba(220, 38, 38, 0.1))'
    },
    502: {
        code: '502',
        title: 'Bad Gateway',
        subtitle: 'The server received an invalid response. Please try again.',
        icon: ServerCrash,
        color: '#f97316',
        gradient: 'linear-gradient(135deg, #ffedd5, #fdba74)',
        darkGradient: 'linear-gradient(135deg, rgba(249, 115, 22, 0.15), rgba(234, 88, 12, 0.1))'
    },
    503: {
        code: '503',
        title: 'Service Unavailable',
        subtitle: 'The server is temporarily unavailable. Please check back soon.',
        icon: Clock,
        color: '#64748b',
        gradient: 'linear-gradient(135deg, #f1f5f9, #cbd5e1)',
        darkGradient: 'linear-gradient(135deg, rgba(100, 116, 139, 0.15), rgba(71, 85, 105, 0.1))'
    },
    default: {
        code: 'ERROR',
        title: 'Something Went Wrong',
        subtitle: 'An unexpected error occurred. Please try again or contact support.',
        icon: Zap,
        color: '#ef4444',
        gradient: 'linear-gradient(135deg, #fee2e2, #fca5a5)',
        darkGradient: 'linear-gradient(135deg, rgba(239, 68, 68, 0.15), rgba(220, 38, 38, 0.1))'
    }
}

export const ErrorFallback = ({ error, resetErrorBoundary }) => {
    const [isDark, setIsDark] = useState(false)

    useEffect(() => {
        const theme = document.documentElement.getAttribute('data-theme')
        setIsDark(theme === 'dark')
    }, [])

    const errorConfig = useMemo(() => {
        if (!error) return ERROR_CONFIG.default

        const statusCode = error?.response?.status || error?.status || error?.code

        if (statusCode && ERROR_CONFIG[statusCode]) {
            return ERROR_CONFIG[statusCode]
        }

        const message = error?.message?.toLowerCase() || ''
        if (message.includes('network') || message.includes('connection')) {
            return ERROR_CONFIG[503]
        }
        if (message.includes('timeout')) {
            return ERROR_CONFIG[429]
        }
        if (message.includes('permission') || message.includes('forbidden')) {
            return ERROR_CONFIG[403]
        }
        if (message.includes('not found') || message.includes('missing')) {
            return ERROR_CONFIG[404]
        }
        if (message.includes('unauthorized') || message.includes('auth')) {
            return ERROR_CONFIG[401]
        }

        return ERROR_CONFIG.default
    }, [error])

    const IconComponent = errorConfig.icon

    const handleGoHome = () => {
        window.location.href = '/homepage'
    }

    const handleReload = () => {
        if (resetErrorBoundary) {
            resetErrorBoundary()
        } else {
            window.location.reload()
        }
    }

    return (
        <div className="error-fallback-page">
            {/* Static Background Image */}
            <div className="error-bg-layer">
                <img src={errorBg} alt="" />
            </div>

            {/* Overlay */}
            <div className="error-overlay" />

            {/* Content */}
            <div className="error-content">
                {/* Icon & Code */}
                <div className="error-icon-wrapper">
                    <div className="error-icon-circle" style={{ background: errorConfig.gradient }}>
                        <IconComponent size={48} style={{ color: errorConfig.color }} />
                    </div>
                    <span className="error-code">{errorConfig.code}</span>
                </div>

                {/* Text */}
                <h1 className="error-title">{errorConfig.title}</h1>
                <p className="error-subtitle">{errorConfig.subtitle}</p>

                {/* Error Details (from backend if available) */}
                {(() => {
                    const backendMsg = error?.response?.data?.message || error?.response?.data?.error || error?.message
                    if (!backendMsg) return null
                    return (
                        <div className="error-details">
                            <code>{backendMsg}</code>
                        </div>
                    )
                })()}

                {/* Actions */}
                <div className="error-actions">
                    <button className="error-btn error-btn-secondary" onClick={handleReload}>
                        <RefreshCw size={18} />
                        <span>Try Again</span>
                    </button>
                    <button className="error-btn error-btn-primary" onClick={handleGoHome}>
                        <Home size={18} />
                        <span>Go to Homepage</span>
                    </button>
                </div>

                {/* Back Button */}
                <button className="error-back-link" onClick={() => window.history.back()}>
                    <ArrowLeft size={16} />
                    <span>Go back to previous page</span>
                </button>
            </div>
        </div>
    )
}
